import { clone } from 'lodash';
import { MachineHelper } from '../../classes/machine.helper';
import { MlbOutcomeCode } from '../../enums/mlb-stats-api/guid-metadata-types.enum';
import { BuildPriority, PitchType } from '../../enums/pitches.enums';
import { HasBsHash } from '../common/i-extensions';
import { Nullable } from '../common/i-generics';
import { IMachineState } from '../i-machine-state';
import { IMlbPlayerCoordinate } from '../mlb-stats-api/i-player';
import { MongoChildRecord } from '../mongo/_base';
import { IOnDemandMachineState } from '../on-demand/i-machine-state';
import { ITrainedMachineState } from '../training/i-trained-machine-state';
import { DEFAULT_BALL_STATE, IBallState } from './i-ball-state';
import { DEFAULT_SEAMS, ISeamOrientation } from './i-base';
import { IPlateLoc } from './i-plate-loc';
import {
  DEFAULT_TRAJECTORY,
  ITrajectory,
  ITrajektRefBreak,
} from './i-trajectory';

export interface IPitchMetadata {
  // shortcut for identifying relevant pitches given a user
  team_id?: string;

  name: string;
  type: PitchType;
  year: string;

  // values from 0-100 (0 => ignored by shuffle)
  frequency?: number;

  // manual equivalent of the above mlb_repertoire
  pitcher: Nullable<string>;
  // manual equivalent of the above mlb_repertoire
  team: Nullable<string>;
  // used for identifying pitchers w/o MLB Stats API
  customID: Nullable<string>;
}

// if created via the MLB Stats API
interface IMlbMetadata {
  mlb_gamePk: number;
  mlb_guid: string;

  hitter: string;
  outcome: MlbOutcomeCode;
  game: string;

  // for Game Data player picker
  teamPk: Nullable<number>;

  // populated when a pitch is created via game data > bulk repertoire importer
  // used for identifying which pitches to recreate when merging
  mlb_repertoire: Nullable<IMlbPlayerCoordinate>;
}

// temp storage of TMS for aiming, getting training status, etc...
interface IMixinDetails {
  // if undefined => assume pitch is not trained
  _mx_tms?: ITrainedMachineState;
  _mx_odms?: IOnDemandMachineState;
}

export interface IPitchVideos {
  windupVideoID: Nullable<string>;
  stretchVideoID: Nullable<string>;
}

interface IDefinition {
  bs: IBallState;
  traj: ITrajectory;
  /** used for post-build rotations, should only be set when:
   * - user creates a pitch
   * - user updates a pitch
   * - server rebuilds a pitch (e.g. for model changes) and there is no value already */
  plate_loc_backup?: IPlateLoc;

  // if provided in CSV uploader
  seams?: ISeamOrientation;

  // in trajekt frame of ref
  breaks?: ITrajektRefBreak;

  priority?: BuildPriority;

  // created via ms debug window
  debugMs?: IMachineState;
}

export type IPitch = MongoChildRecord &
  HasBsHash &
  IDefinition &
  IPitchVideos &
  IMixinDetails &
  Partial<IPitchMetadata> &
  Partial<IMlbMetadata>;

export const DEFAULT_PITCH: IPitch = {
  _id: '',
  _created: '',
  _changed: '',
  _parent_id: '',
  _parent_def: 'pitch-lists',
  _parent_field: 'pitches',

  bs: clone(DEFAULT_BALL_STATE),
  traj: clone(DEFAULT_TRAJECTORY),
  seams: clone(DEFAULT_SEAMS),

  bs_hash: MachineHelper.getBsHash(DEFAULT_BALL_STATE, BuildPriority.Spins),

  priority: BuildPriority.Spins,

  windupVideoID: null,
  stretchVideoID: null,
};

export type ITrainingPitch = Omit<IPitch, '_mx_tms'> & {
  _mx_ms: IMachineState;

  // for RT and HWD
  skipped?: boolean;
};
